/**
 * @module API
 * Cross-window iframe API contracts and client/server helpers.
 */

export type KeybanApiStatus = "operational" | "down";

import { UUID } from "crypto";

import { IFrameApiError, KeybanBaseError, SdkError } from "~/errors";
import type { KeybanNetwork, KeybanUser } from "~/index";

type Hex = `0x${string}`;

/**
 * Arguments for sending a One-Time Password (OTP).
 * @example
 * // Email OTP
 * const args: AuthSendOtpArgs = {
 *   type: "email-otp",
 *   emailInputName: "email",
 * };
 * @example
 * // Phone/SMS OTP
 * const args: AuthSendOtpArgs = {
 *   type: "phone-otp",
 *   phoneCallingCode: "+33",
 *   phoneInputName: "phone",
 * };
 */
export type AuthSendOtpArgs =
  | {
      type: "email-otp";
      emailInputName: string;
    }
  | {
      type: "phone-otp";
      phoneCallingCode: string;
      phoneInputName: string;
    };

/**
 * Arguments for signing up a user using email and password.
 */
export type AuthSignUpArgs = {
  type: "email-password";
  emailInputName: string;
  passwordInputName: string;
  passwordConfirmInputName?: string;
  nameInputName?: string;
};

/**
 * Arguments for signing in a user.
 */
export type AuthSignInArgs =
  | {
      type: "email-password";
      emailInputName: string;
      passwordInputName: string;
    }
  | {
      type: "email-otp";
      emailInputName: string;
      otpInputName: string;
    }
  | {
      type: "phone-otp";
      phoneCallingCode: string;
      phoneInputName: string;
      otpInputName: string;
    }
  | { type: "social-google" };

export type AuthUpdateUserArgs = {
  name: string;
  birthDate: string;
};

/**
 * Authentication service contract.
 * @see {@link IKeybanApi}
 */
export interface IKeybanAuth {
  /** Checks whether the current user is authenticated. */
  isAuthenticated: () => Promise<boolean>;
  /** Retrieves the authenticated user profile, or null. */
  getUser: () => Promise<KeybanUser | null>;
  /** Sends an OTP to email or phone. */
  sendOtp: (args: AuthSendOtpArgs) => Promise<void>;
  /** Registers a new user account. */
  signUp: (args: AuthSignUpArgs) => Promise<KeybanUser>;
  /** Signs in a user using password or OTP. */
  signIn: (args: AuthSignInArgs) => Promise<KeybanUser>;
  /** Signs out the current user. */
  signOut: () => Promise<void>;
  /** Updates the user profile. */
  updateUser: (args: AuthUpdateUserArgs) => Promise<KeybanUser>;
}

/**
 * MPC signer contract for a given curve (ECDSA/EdDSA).
 */
export interface IKeybanSigner {
  /** Performs a Distributed Key Generation ceremony, returning a client share. */
  dkg: () => Promise<string>;
  /** Signs a pre-hashed message with the server share and provided client share. */
  sign: (clientShare: string, message: string) => Promise<Hex>;
  /** Derives the public key associated with the provided client share. */
  publicKey: (clientShare: string) => Promise<Hex>;
}

/**
 * Account service contract.
 */
export interface IKeybanAccount {
  /** Returns the on-chain address of the user account. */
  getAddress: () => Promise<string>;
  getId: () => Promise<UUID>;
}

/**
 * Storage for persisting and retrieving the MPC client share on the integrator side.
 */
export interface IKeybanClientShareStorage {
  /** Retrieves a stored client share by key. */
  get: (key: string) => Promise<string | null>;
  /** Stores a client share under the given key. */
  set: (key: string, clientShare: string) => Promise<void>;
}

/**
 * Loyalty reward tier information.
 */
export type LoyaltyRewardTier = {
  name: string;
  requiredVisits: number;
  currencyToPointRatio: number;
  bgImageUrl?: string;
};

/**
 * Loyalty operations for wallet passes and tier retrieval.
 */
export interface IKeybanLoyalty {
  /** Returns the current loyalty optimistic balance. */
  getOptimisticBalance: () => Promise<string>;
  /** Returns the current loyalty reward tier, or null if none. */
  getRewardTier: () => Promise<LoyaltyRewardTier | null>;
  /** Returns a Google Wallet URL to add the pass. */
  addToGoogleWallet: () => Promise<string>;
  /** Adds an Apple Wallet pass. */
  addToAppleWallet: () => Promise<void>;
}

/**
 * Digital Product Passport (DPP) operations.
 */
export interface IKeybanDpp {
  getCollection: (
    collectionId: string,
  ) => Promise<{ id: string; address: string }>;

  claim: (
    collectionId: string,
    dppId: string,
  ) => Promise<{ transactionHash: string }>;
}

/**
 * Aggregated API surface exposed through the iframe transport.
 */
export interface IKeybanApi {
  auth: IKeybanAuth;
  ecdsa: IKeybanSigner;
  eddsa: IKeybanSigner;
  account: IKeybanAccount;
  clientShareStorage: IKeybanClientShareStorage;
  dpp: IKeybanDpp;
  loyalty: IKeybanLoyalty;
}

/*
 * API internal types
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type CastFn<T> = T extends (...args: any[]) => any ? T : never;

type Service = keyof IKeybanApi;
type Method<S extends Service> = keyof IKeybanApi[S] & string;
type ClassMethod<S extends Service, M extends Method<S>> = CastFn<
  IKeybanApi[S][M]
>;

type ApiCall<
  S extends Service,
  M extends Method<S>,
  CM extends ClassMethod<S, M> = ClassMethod<S, M>,
> = {
  __KEYBAN_API: true;

  service: S;
  method: M;
  args: Parameters<CM>;
};

type ApiResult<
  S extends Service,
  M extends Method<S>,
  CM extends ClassMethod<S, M> = ClassMethod<S, M>,
> = [Awaited<ReturnType<CM>>, null] | [null, string];

/*
 * API server
 */

/**
 * @private
 */
export abstract class ApiServer implements IKeybanApi {
  abstract auth: IKeybanAuth;
  abstract ecdsa: IKeybanSigner;
  abstract eddsa: IKeybanSigner;
  abstract account: IKeybanAccount;
  abstract loyalty: IKeybanLoyalty;
  abstract clientShareStorage: IKeybanClientShareStorage;
  abstract dpp: IKeybanDpp;

  constructor() {
    window.addEventListener("message", this.#handleMessage);
  }

  // Forced validation of service's methods, see listener below
  static #definitions: {
    [S in Service]: {
      [M in Method<S>]: true;
    };
  } = {
    auth: {
      isAuthenticated: true,
      getUser: true,
      sendOtp: true,
      signUp: true,
      signIn: true,
      signOut: true,
      updateUser: true,
    },
    ecdsa: {
      dkg: true,
      sign: true,
      publicKey: true,
    },
    eddsa: {
      dkg: true,
      sign: true,
      publicKey: true,
    },
    account: {
      getAddress: true,
      getId: true,
    },
    loyalty: {
      getOptimisticBalance: true,
      getRewardTier: true,
      addToGoogleWallet: true,
      addToAppleWallet: true,
    },
    dpp: {
      getCollection: true,
      claim: true,
    },
    clientShareStorage: {
      get: true,
      set: true,
    },
  };

  abstract checkEventOrigin(eventOrigin: string): Promise<boolean>;

  #handleMessage = async <S extends Service, M extends Method<S>>(
    event: MessageEvent<ApiCall<S, M>>,
  ) => {
    if (!event.data.__KEYBAN_API) return;

    try {
      // Check the message originated from an allowed domain for this
      // specific application.
      const originAllowed = await this.checkEventOrigin(event.origin);
      if (!originAllowed)
        throw new IFrameApiError(
          IFrameApiError.types.InvalidOrigin,
          "ApiServer",
        );

      const { service, method } = event.data;

      // An attacker could possibly try to call a method on the service
      // object that is not intended to be exposed. This  ensures the
      // method is effectively allowed.
      if (!ApiServer.#definitions[service]?.[method])
        throw new IFrameApiError(
          IFrameApiError.types.InvalidCall,
          `ApiServer:${service}.${method}`,
        );

      const fn = this[service]?.[method] as ClassMethod<S, M>;
      if (!fn)
        throw new IFrameApiError(
          IFrameApiError.types.InvalidCall,
          `ApiServer:${service}.${method}`,
        );

      const result = await fn.apply(this[service], event.data.args);

      event.ports[0].postMessage([result, null]);
    } catch (error) {
      let err = error;

      if (!(err instanceof KeybanBaseError)) {
        console.error(error);
        err = new SdkError(
          SdkError.types.UnknownIframeApiError,
          "ApiServer",
          error as Error,
        );
      }

      event.ports[0].postMessage([null, JSON.stringify(err)]);
    }
  };

  static openPopup(url: URL | string) {
    const channel = new MessageChannel();

    return new Promise<void>((resolve) => {
      channel.port1.onmessage = () => {
        channel.port1.close();
        resolve();
      };

      const message = {
        type: "keyban:popup",
        url: url.toString(),
      };

      window.parent.postMessage(message, "*", [channel.port2]);
    });
  }
}

/*
 * API client
 */

type ApiClientOptions = {
  apiUrl: URL | string;
  appId: string;
  network: KeybanNetwork;
};

/**
 * Iframe-backed API client. Use this to call services implemented by {@link ApiServer}.
 * @example
 * const client = ApiClient.getInstance({ apiUrl: "https://api.prod.keyban.io", appId: "app_123", network: "EthereumAnvil" });
 * const isAuth = await client.api.auth.isAuthenticated();
 */
export class ApiClient {
  #iframeUrl: URL;
  #iframe: Promise<HTMLIFrameElement>;

  static #instances: Map<string, ApiClient> = new Map();

  static #getIframeUrl({ apiUrl, appId, network }: ApiClientOptions) {
    const iframeUrl = new URL("/sdk-client/api", apiUrl);
    iframeUrl.searchParams.set("appId", appId);
    iframeUrl.searchParams.set("network", network);
    return iframeUrl;
  }

  /**
   * Returns a shared instance per unique iframe URL.
   * @param options - API client options.
   * @returns ApiClient instance bound to the provided options.
   */
  static getInstance(options: ApiClientOptions): ApiClient {
    const key = ApiClient.#getIframeUrl(options).toString();
    if (!ApiClient.#instances.has(key))
      ApiClient.#instances.set(key, new ApiClient(options));

    return ApiClient.#instances.get(key)!;
  }

  api: IKeybanApi;

  private constructor(options: ApiClientOptions) {
    this.#iframeUrl = ApiClient.#getIframeUrl(options);

    window.addEventListener("message", this.#popupHandler);

    this.#iframe = new Promise((resolve, reject) => {
      const iframe = Object.assign(document.createElement("iframe"), {
        src: this.#iframeUrl,
        hidden: true,
        onload: () => resolve(iframe),
        onerror: reject,
      });

      document.body.appendChild(iframe);
    });

    this.api = new Proxy({} as IKeybanApi, {
      // API Proxy
      get: <S extends Service>(_target: object, service: S) =>
        new Proxy({} as IKeybanApi[S], {
          // Service Proxy
          get: <M extends Method<S>>(_target: object, method: M) =>
            new Proxy(() => {}, {
              // Method Proxy
              apply: <CM extends ClassMethod<S, M>>(
                _target: () => unknown,
                _thisArg: object,
                args: Parameters<CM>,
              ) => this.#call(service, method, args),
            }),
        }),
    });
  }

  #popupHandler = async (event: MessageEvent) => {
    const iframe = await this.#iframe;
    if (event.source !== iframe.contentWindow) return;
    if (typeof event.data !== "object") return;
    if (event.data.type != "keyban:popup") return;

    const width = 500;
    const height = 685;
    const left = window.screenX + (window.innerWidth - width) / 2;
    const top = window.screenY + (window.innerHeight - height) / 2;

    const popup = window.open(
      event.data.url,
      `keyban:popup`,
      `left=${left},top=${top},width=${width},height=${height},popup,resizable,scrollbars`,
    );

    // Wait for popup to be closed
    await new Promise<void>((resolve) => {
      const interval = setInterval(() => {
        if (!popup?.closed) return;

        clearInterval(interval);
        resolve();
      }, 100);
    });

    event.ports[0].postMessage("closed");
  };

  async #call<
    S extends Service,
    M extends Method<S>,
    CM extends ClassMethod<S, M> = ClassMethod<S, M>,
  >(
    service: S,
    method: M,
    args: Parameters<CM>,
  ): Promise<Awaited<ReturnType<CM>>> {
    const iframe = await this.#iframe;
    const channel = new MessageChannel();

    return await new Promise((resolve, reject) => {
      channel.port1.onmessage = ({
        data: [result, error],
      }: MessageEvent<ApiResult<S, M>>) => {
        channel.port1.close();
        return error != null
          ? reject(new KeybanBaseError(JSON.parse(error)))
          : resolve(result);
      };

      const message: ApiCall<S, M> = {
        __KEYBAN_API: true,
        service,
        method,
        args,
      };

      iframe.contentWindow?.postMessage(message, this.#iframeUrl.origin, [
        channel.port2,
      ]);
    });
  }
}
